/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Table, Popconfirm, Tooltip, Select, Row } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import fileDownload from 'js-file-download';

import styles from '../styles';
import { errorNotification, successNotification } from '../Snackbar';
import {
  sorter,
  onFilter,
  filterDropdown,
  filterIcon,
  get,
  dateSorter,
  onDateFilter,
  dateFilterDropdown,
} from '../utils';
import Api from '../Api';
import constants from '../constants';
import Breadcrumbs from '../Components/Breadcrumbs';
import { clearStoreInwards, getStoreInwards } from '../Actions';
import { currentFinancialYear, financialYears } from '../financial-years';

const StoreInwardList = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);
  const storeInwards = useSelector(state => state.storeInwards);

  const [Year, SetYear] = useState(currentFinancialYear);

  useEffect(() => {
    return () => {
      dispatch(clearStoreInwards());
    };
  }, []);

  useEffect(() => {
    dispatch(getStoreInwards({ year: Year }));

    return () => {
      dispatch(clearStoreInwards());
    };
  }, [Year]);

  const onDelete = useCallback(async id => {
    try {
      await Api.delete(`/store-inwards/${id}`);
      successNotification('Store inward deleted');
      dispatch(getStoreInwards());
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const updateColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.updateStoreInward) ? (
            <Tooltip placement="bottom" title="Update">
              <Link to={`/storeinward/update/${record.id}`}>
                <EditOutlined className={classes.tableIcon} />
              </Link>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const qualityCheckColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.createQualityCheck) ? (
            <Tooltip placement="bottom" title="Check">
              <Link to={`/qualitycheck/create/${record.id}`}>
                <CheckCircleOutlined className={classes.tableIcon} />
              </Link>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const deleteColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.deleteStoreInward) ? (
            <Tooltip placement="bottom" title="Delete">
              <Popconfirm
                title="Are you sure you want to delete?"
                onConfirm={() => onDelete(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <DeleteOutlined className={classes.tableIcon} />
              </Popconfirm>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const onFileDownload = useCallback(async id => {
    try {
      const { data } = await Api.getFile(`/store-inwards/download/${id}`);
      fileDownload(data, `store-inwards-${id}.pdf`);
      successNotification('File downloaded');
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const downloadColumn = useMemo(
    () => record =>
      (
        <Tooltip placement="bottom" title="Download">
          <DownloadOutlined
            className={classes.tableIcon}
            onClick={() => onFileDownload(record.id)}
          />
        </Tooltip>
      ),
    []
  );

  const rowTextColor = useMemo(
    () => (text, record) =>
      !record.qcs ? <span style={{ color: '#d9363e' }}>{text}</span> : text,
    []
  );

  const rowDateColor = useMemo(
    () => (text, record) =>
      !record.qcs ? (
        <span style={{ color: '#d9363e' }}>
          {text ? moment(text).format('DD MMM YYYY') : null}
        </span>
      ) : text ? (
        moment(text).format('DD MMM YYYY')
      ) : null,
    []
  );

  const columns = useMemo(() => {
    const column = [
      {
        title: 'Inward #',
        dataIndex: 'inwardNo',
        sorter: sorter('inwardNo'),
        onFilter: onFilter('inwardNo'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'Inward Date',
        dataIndex: 'inwardDate',
        sorter: dateSorter('inwardDate'),
        onFilter: onDateFilter('inwardDate'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: rowDateColor,
      },
      {
        title: 'Vendor',
        dataIndex: 'vendor',
        sorter: sorter('vendor'),
        onFilter: onFilter('vendor'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'RPO #',
        dataIndex: 'rpoNo',
        sorter: sorter('rpoNo'),
        onFilter: onFilter('rpoNo'),
        filterDropdown,
        filterIcon,
        render: (text, record) => (
          <Link to={`/storeinward/rpo/${record.rpoId}`}>{record.rpoNo}</Link>
        ),
      },
      {
        title: 'QC Nos',
        dataIndex: 'qcNos',
        sorter: sorter('qcNos'),
        onFilter: onFilter('qcNos'),
        filterDropdown,
        filterIcon,
        render: (text, record) => (
          <>
            {(record.qcs || []).map(qc => (
              <>
                <Link
                  key={qc.id}
                  to={`/qualitycheck/view/${record.id}/${qc.id}`}
                >
                  {qc.qcNo}
                </Link>
                ,&nbsp;
              </>
            ))}
          </>
        ),
      },
      {
        title: 'Customer',
        dataIndex: 'customer',
        sorter: sorter('customer'),
        onFilter: onFilter('customer'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'BPO #',
        dataIndex: 'bpoNo',
        sorter: sorter('bpoNo'),
        onFilter: onFilter('bpoNo'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'Products',
        dataIndex: 'products',
        sorter: sorter('products'),
        onFilter: onFilter('products'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'Fabrics',
        dataIndex: 'fabrics',
        sorter: sorter('fabrics'),
        onFilter: onFilter('fabrics'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'Styles',
        dataIndex: 'styles',
        sorter: sorter('styles'),
        onFilter: onFilter('styles'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'Items',
        dataIndex: 'items',
        sorter: sorter('items'),
        onFilter: onFilter('items'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'Accessories',
        dataIndex: 'accessories',
        sorter: sorter('accessories'),
        onFilter: onFilter('accessories'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'Alter/Reprocess',
        dataIndex: 'isAlter',
        sorter: sorter('isAlter'),
        onFilter: onFilter('isAlter'),
        filterDropdown,
        filterIcon,
        render: (text, record) =>
          !record.qcs ? (
            <span style={{ color: '#d9363e' }}>
              {record.isAlter ? 'Yes' : 'No'}
            </span>
          ) : record.isAlter ? (
            'Yes'
          ) : (
            'No'
          ),
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        sorter: sorter('remark'),
        onFilter: onFilter('remark'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
    ];

    if (permissions.includes(constants.permissions.updateStoreInward)) {
      column.push({
        title: 'Update',
        render: updateColumn,
      });
    }

    if (permissions.includes(constants.permissions.deleteStoreInward)) {
      column.push({
        title: 'Delete',
        render: deleteColumn,
      });
    }

    column.push(
      {
        title: 'Created',
        children: [
          {
            title: 'By',
            dataIndex: 'createdBy',
            sorter: sorter('createdBy'),
            onFilter: onFilter('createdBy'),
            filterDropdown,
            filterIcon,
            render: rowTextColor,
          },
          {
            title: 'Date',
            dataIndex: 'createdAt',
            sorter: dateSorter('createdAt'),
            onFilter: onDateFilter('createdAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: rowDateColor,
          },
        ],
      },
      {
        title: 'Last Updated',
        children: [
          {
            title: 'By',
            dataIndex: 'updatedBy',
            sorter: sorter('updatedBy'),
            onFilter: onFilter('updatedBy'),
            filterDropdown,
            filterIcon,
            render: rowTextColor,
          },
          {
            title: 'Date',
            dataIndex: 'updatedAt',
            sorter: dateSorter('updatedAt'),
            onFilter: onDateFilter('updatedAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: rowDateColor,
          },
        ],
      },
      {
        title: 'Download',
        render: downloadColumn,
        fixed: 'right',
        width: '3%',
      }
    );

    if (permissions.includes(constants.permissions.createQualityCheck)) {
      column.push({
        title: 'Quality Check',
        render: qualityCheckColumn,
        fixed: 'right',
        width: '2%',
      });
    }

    return column;
  }, []);

  const rowKeys = useMemo(
    () => record =>
      `Id${record.id}InNo${record.inwardNo}Dt${record.inwardDate}`,
    []
  );

  const pagination = useMemo(
    () => ({
      defaultPageSize: 20,
      position: ['bottomLeft'],
      showTotal: total => `Total ${total} Records`,
    }),
    []
  );

  const scroll = useMemo(() => ({ x: 3200, y: window.innerHeight - 300 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Store Inward"
        moduleLink="/storeinward/list"
        subModuleName="List"
      />
      <div className={classes.tableStyle}>
        <Row>
          <Select
            value={Year}
            style={{ width: 150 }}
            onChange={value => SetYear(value)}
          >
            {financialYears.map(el => (
              <Select.Option key={el} value={el}>
                {el}
              </Select.Option>
            ))}
          </Select>
        </Row>
        <br />
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={storeInwards}
          rowKey={rowKeys}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
    </>
  );
};

StoreInwardList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(StoreInwardList);
